import React, { ReactNode } from 'react';

interface PageHeaderProps {
  /** Main title text */
  title: string;
  /** Subtitle/description text */
  subtitle?: string;
  /** Optional action button/content (e.g., RSS button on blog) */
  action?: ReactNode;
  /** Optional content below the header (e.g., stats section on posts) */
  belowHeader?: ReactNode;
  /** Additional CSS classes for the outer container */
  className?: string;
}

/**
 * Standardized page header component used across all landing pages.
 *
 * Features:
 * - Consistent styling with bg-gray-700 header
 * - max-w-[1400px] container matching site width
 * - Support for title, subtitle, and action buttons
 * - Support for additional content below header (stats, filters, etc.)
 *
 * Usage examples:
 *
 * Basic (About, Contact):
 * ```tsx
 * <PageHeader title="About" subtitle="30 years of fire, flavor, and backyahd passion" />
 * ```
 *
 * With action button (Blog):
 * ```tsx
 * <PageHeader
 *   title="Blog"
 *   subtitle="Step-by-step guides and techniques"
 *   action={<a href="/rss.xml"><Rss /> RSS</a>}
 * />
 * ```
 *
 * With content below (Posts):
 * ```tsx
 * <PageHeader
 *   title="Posts"
 *   subtitle="Social media content"
 *   belowHeader={<StatsSection />}
 * />
 * ```
 */
export function PageHeader({
  title,
  subtitle,
  action,
  belowHeader,
  className = '',
}: PageHeaderProps) {
  return (
    <>
      {/* Main Header */}
      <div className={`mb-6 ${className}`}>
        <div className="border border-gray-600 bg-gray-700 py-4 sm:py-6">
          <div className="px-4">
            {action ? (
              // Header with action button (Blog)
              <div className="flex items-start justify-between gap-3 sm:items-center">
                <div className="min-w-0">
                  <h1 className="text-balance text-3xl font-bold text-white sm:text-4xl">
                    {title}
                  </h1>
                  {subtitle && (
                    <p
                      className="mt-1 text-base !text-white !opacity-100 sm:text-lg"
                      style={{ color: '#ffffff' }}
                    >
                      {subtitle}
                    </p>
                  )}
                </div>
                {action}
              </div>
            ) : (
              // Simple header (most pages)
              <>
                <h1 className="text-balance text-3xl font-bold text-white sm:text-4xl">{title}</h1>
                {subtitle && (
                  <p
                    className="mt-1 text-base !text-white !opacity-100 sm:text-lg"
                    style={{ color: '#ffffff' }}
                  >
                    {subtitle}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Optional content below header (Stats, filters, etc.) */}
      {belowHeader && belowHeader}
    </>
  );
}
